import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { generatePath } from 'react-router';

import { withTranslation } from '@wix/yoshi-flow-editor';
import { getChannelForWidget } from '@wix/wix-vod-shared/common';
import focus from '@wix/wix-vod-shared/dist/esm/widget/utils/accessibility-focus';
import Spinner from '@wix/wix-vod-shared/dist/esm/widget/ui-components/loading-spinner/loading-spinner';

import { logBi } from '../../../worker/actions/bi';
import CloseButton from '@wix/wix-vod-shared/dist/esm/widget/ui-components/close-button/close-button';
import { PaymentType } from '@wix/wix-vod-constants/api-types';
import { isPayed } from '../../../selectors/payments';
import { getVideoById } from '../../../selectors/videos';
import { logOutCurrentMember } from '../../../utils/auth';
import { publishVideoOverlayCloseRequest } from '../../../utils/video-overlay/video-overlay-close-request';
import { getWixSDK } from '../../../utils/wix-sdk';

import CLOSE_REASONS from '../../constants/close-reasons';

import { getPaymentInfo } from '../../../redux/actions/channel/payment';
import { getPublicVideo } from '../../../redux/actions/channel/video/get';
import { getCurrentSiteUser } from '../../../selectors/current-site-user';
import { isSuccessPayment } from '../../../selectors/payment';
import { isPlayInFrame } from '../../../selectors/app-settings';
import CashierPaymentModal from '../../../components/cashier/payment-modal/cashier-payment-modal';
import { storeForReload } from '../../../utils/reload';

import styles from './payment-view.scss';
import { PAYMENT_STEPS } from './constants';

const mapStateToProps = (state, { match }) => {
  const videoData = getVideoById(state, match.params.videoId);
  const channelData = getChannelForWidget(state);
  const itemData = match.params.videoId ? videoData : channelData;
  return {
    isPlayInFrame: isPlayInFrame(state),
    videoData,
    channelData,
    itemData,
    isChannelPayed: isPayed(channelData),
    isVideoPayed: match.params.videoId && isPayed(videoData),
    currentSiteUser: getCurrentSiteUser(state),
    isReady: Boolean(itemData),
    isSuccessPayment: isSuccessPayment(state),
  };
};

const mapDispatchToProps = {
  getPaymentInfo,
  getPublicVideo,
  logBi,
  logOutCurrentMember,
  storeForReload,
  publishVideoOverlayCloseRequest,
};

export default withTranslation()(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(
    class PaymentView extends React.Component {
      static propTypes = {
        history: PropTypes.object.isRequired,
        match: PropTypes.object.isRequired,
        channelData: PropTypes.object.isRequired,
        videoData: PropTypes.object,
        itemData: PropTypes.object,
        orderId: PropTypes.string,
        isReady: PropTypes.bool,
        isChannelPayed: PropTypes.bool,
        isVideoPayed: PropTypes.bool,
        getPublicVideo: PropTypes.func.isRequired,
        publishVideoOverlayCloseRequest: PropTypes.func.isRequired,
        close: PropTypes.func.isRequired,
        getPaymentInfo: PropTypes.func.isRequired,
        currentSiteUser: PropTypes.object,
        isSuccessPayment: PropTypes.bool,
        isPlayInFrame: PropTypes.bool.isRequired,
      };

      constructor(props) {
        super(props);

        this.state = {
          step: PAYMENT_STEPS.INFO,
        };
      }

      componentDidMount() {
        focus(this.containerRef);
        const { channelId, videoId } = this.props.match.params;

        this.props.getPaymentInfo(channelId);
        if (videoId) {
          this.props.getPublicVideo(channelId, videoId);
        }
      }

      componentDidUpdate() {
        const {
          isChannelPayed,
          isVideoPayed,
          currentSiteUser,
          match: {
            params: { paymentType },
          },
        } = this.props;

        if (
          isChannelPayed ||
          isVideoPayed ||
          (currentSiteUser && paymentType === PaymentType.MEMBER_ONLY)
        ) {
          getWixSDK().closeWindow();
        }
      }

      saveContainerRef = (ref) => {
        this.containerRef = ref;
      };

      selectPaymentType = (paymentType) => {
        const { channelData, videoData, history, match } = this.props;

        const nextPath = generatePath(match.path, {
          paymentType,
          channelId: channelData.id,
          videoId: videoData.id,
        });

        history.replace(nextPath);
      };

      handleUserLogin = () => {
        const { isPlayInFrame, match } = this.props;
        const { videoId, paymentType } = match.params;

        if (paymentType === PaymentType.MEMBER_ONLY) {
          this.playVideoAfterPageRefresh({ openOverlay: !isPlayInFrame });
        } else {
          this.props.storeForReload({
            selectedVideoId: videoId,
          });
        }
      };

      handleUserLogout = () => {
        this.props.logBi('widget.logOut.clicked');
        const { videoId } = this.props.match.params;
        this.props.storeForReload({
          selectedVideoId: videoId,
        });

        this.props.logOutCurrentMember();
      };

      playVideoAfterPageRefresh = ({ openOverlay = true } = {}) => {
        const {
          close,
          match: {
            params: { videoId },
          },
          publishVideoOverlayCloseRequest,
          storeForReload,
        } = this.props;

        // in video widget we could not be sure if video overlay opened,
        // so close video overlay, to prevent multiple overlays on `PLAY_VIDEO` requests
        publishVideoOverlayCloseRequest();

        storeForReload({
          selectedVideoId: videoId,
          openOverlay,
          autoplay: false,
        });

        close(CLOSE_REASONS.SUCCEED);
      };

      handleModalClosing = () => {
        const { isSuccessPayment } = this.props;

        if (isSuccessPayment) {
          this.playVideoAfterPageRefresh();
        } else {
          this.closeModal();
        }
      };

      renderView() {
        const {
          match: {
            params: { paymentType, videoId },
          },
        } = this.props;

        return (
          <CashierPaymentModal
            onPaymentTypeChange={this.selectPaymentType}
            onPaymentStart={this.paymentStart}
            onClose={this.handleModalClosing}
            videoId={videoId}
            onUserLogin={this.handleUserLogin}
            onLogOut={this.handleUserLogout}
            paymentType={paymentType}
            step={this.state.step}
            onStepChange={this.handleStepChange}
          />
        );
      }

      handleStepChange = (step) => {
        this.setState({ step });
      };

      closeModal = () => {
        this.props.close(CLOSE_REASONS.CLOSED_BY_USER);
      };

      getCloseButtonTheme() {
        const { step } = this.state;

        if (step === PAYMENT_STEPS.INFO) {
          return 'dark';
        }

        return 'light';
      }

      renderCloseButton() {
        const { step } = this.state;

        return (
          <CloseButton
            className={classnames(styles['close-button'], styles.responsive, {
              [styles.paymentStep]: step === PAYMENT_STEPS.PAYMENT,
            })}
            onClick={this.closeModal}
            theme={this.getCloseButtonTheme()}
            dataHook="close-payment-modal-button"
            ariaLabel={this.props.t('widget.accessibility.close-purchase-info')}
          />
        );
      }

      render() {
        const { isReady } = this.props;

        return (
          <section
            ref={this.saveContainerRef}
            tabIndex={-1}
            aria-modal="true"
            role="dialog"
            aria-label={this.props.t('widget.accessibility.purchase-info')}
            aria-describedby="payment-item"
          >
            <div className={styles.window} data-hook="payment-view">
              {isReady ? this.renderView() : <Spinner />}
              {this.renderCloseButton()}
            </div>
          </section>
        );
      }
    },
  ),
);
